import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Pagination } from "antd";

import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';

import Logo from "../../assets/logo.png";
import InfoCard from "../../components/Common/InfoCard";
import TableHeader from "../../components/Common/TableHeader";
import LandLordTable from "../../components/Table/LandLordTable";
import Loader from "../../components/Common/Loader";
import NotFound from "../../components/NotFound";
import showToast from "../../utils/toastNotifications";
import { formatDateToISO, getDateRangeFromSelection } from "../../utils/dateFormating";
import { svgToCanvas } from "../../utils/svgToCanvas";

import { getPayoutHistory } from "../../store/thunks/paymentThunks";
import { useThunk } from "../../hooks/useThunk";

import { monthArray } from "../../constants/commonConstants";

const formatPayoutDate = (dateString) => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${month}/${day} ${hours}:${minutes}`;
};


const AdminPayoutPage = () => {
  const [getPayoutHistoryData, isFetchingPayouts, errorPayouts] = useThunk(getPayoutHistory);

  const [payoutHistory, setPayoutHistory] = useState(null);
  const [pagination, setPagination] = useState({});
  const [metrics, setMetrics] = useState({});
  const [isEmptyData, setIsEmptyData] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get("page")) || 1;
  const searchTerm = searchParams.get("search") || "";
  const statusFilter = searchParams.get("status") || "";
  const dateRange = searchParams.get("dateRange") || "";

  const { success, response } = payoutHistory || {};

  const [search, setSearch] = useState(searchTerm);
  const [status, setStatus] = useState(statusFilter);
  const [selectedRange, setSelectedRange] = useState(dateRange);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const itemsPerPage = 10;

  useEffect(() => {
    fetchPayoutHistory();
  }, [searchParams]);

  const fetchPayoutHistory = async (download = false, monthRange=1, page = currentPage, searchValue = search, statusValue = status, rangeValue = selectedRange,) => {
    
    let { startDate, endDate } = getDateRangeFromSelection(rangeValue);

    if(download && !startDate && !endDate) {
      // Apply 6-month limit when no date range is selected
      const today = new Date();
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(today.getMonth() - 6);
      startDate = sixMonthsAgo;
      endDate = today;
      monthRange = 7;
    }

    const params = {
      offset: download ? null : (page - 1) * itemsPerPage,
      limit: download ? null : itemsPerPage,
      searchTerm: searchValue,
      status: statusValue,
      startDate: startDate ? startDate : null,
      endDate: endDate ? endDate : null,
      month: startDate ? monthArray[startDate.getMonth()] : null,
      monthRange: monthRange,
    };

    const data = await getPayoutHistoryData(params);


    if (data?.success && !download) {
      setPayoutHistory(data);
      setIsEmptyData(data?.response?.data?.data?.length === 0);
      setPagination(data?.response?.data?.pagination || {});
      setMetrics(data?.response?.data?.infoCard || {});
    }

    return {data: data?.response?.data, startDate, endDate};
  };

  const handlePageChange = (page) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", page);
      if (search) newParams.set("search", search);
      if (status) newParams.set("status", status);
      if (selectedRange) newParams.set("dateRange", selectedRange);
      return newParams;
    });
  };

  const handleSearch = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (search) newParams.set("search", search);
      else newParams.delete("search");
      if (status) newParams.set("status", status);
      else newParams.delete("status");
      if (selectedRange) newParams.set("dateRange", selectedRange);
      else newParams.delete("dateRange");
      newParams.set("page", 1);
      return newParams;
    });
  };

  const transformPayouts = (data) => {
    return (data || []).map((item, index) => ({
      key: `payout-${index}`,
      date: formatPayoutDate(item.payoutDate),
      landlordName: item.landlordName,
      deposit: item.payoutAccount,
      rent: item.grossAmount,
      commission: item.platformCommission,
      status: item.transactionStatus === "SUCCESS" ? "Paid" : item.transactionStatus,
      payments: [item],
    }));
  };

  const transformedPayouts = transformPayouts(payoutHistory?.response?.data?.data)
    

  const columns = [
    { title: "Date / Time", dataIndex: "date", key: "date" },
    { title: "Landlord Name", dataIndex: "landlordName", key: "landlordName" },
    { title: "Mobile Wallet", dataIndex: "deposit", key: "deposit" },
          {
        title: (
          <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">
            Status
          </span>
        ),
        dataIndex: "status",
        key: "status",
        render: (status) => (
          <span
            className="font-normal"
            style={{
              color:
                status === "Paid"
                  ? "#07C01D"
                  : status === "PROCESSING"
                  ? "#C46500"
                  : status === "FAILED"
                  ? "#FF0505"
                  : "#9ca3af",
            }}
          >
            {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
          </span>
        ),
      },
    {
      title: "Platform Commission",
      dataIndex: "commission",
      key: "commission",
      render: (value) => {
          return (
        <span className="text-[#707070] font-inter">
              {`${value.toFixed(2)} UGX`}
            </span>
      );
     },
    },
  ];

  const subTableConfig = {
    dataField: "payments",
    columns: [
      {
        title: (
          <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">
            Net amount paid
          </span>
        ),
        dataIndex: "netAmount",
        key: "netAmount",
        render: (value) => {
          return `${value.toFixed(2)} UGX`;
        },
      },
      // {
      //   title: (
      //     <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">
      //       Status
      //     </span>
      //   ),
      //   dataIndex: "transactionStatus",
      //   key: "transactionStatus",
      //   render: (status) => (
      //     <span
      //       className="font-normal"
      //       style={{
      //         color:
      //           status === "SUCCESS"
      //             ? "#07C01D"
      //             : status === "Processing"
      //             ? "#cb543c"
      //             : status === "FAILED"
      //             ? "#FF0505"
      //             : "#9ca3af",
      //       }}
      //     >
      //       {status}
      //     </span>
      //   ),
      // },
      {
        title: (
          <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">
            Reference ID
          </span>
        ),
        dataIndex: "relatedPayment",
        key: "relatedPayment",
      },
    ],
  };

  const createInfoCardSVG = (data) => {
      // Calculate complexity scores for each value
      const rentCollectedStr = data.totalRentCollected.toFixed(2);
      const rentExpectedStr = data.totalRentExpected.toFixed(2);
      const propertiesStr = data.totalUniqueProperties.toString();
      const unitsStr = data.totalUniqueUnits.toString();

      // Determine space needs
      const rentCollectedNeedsSpace = rentCollectedStr.length > 12;
      const rentExpectedNeedsSpace = rentExpectedStr.length > 12;
      const bothRentNeedSpace = rentCollectedNeedsSpace || rentExpectedNeedsSpace;

      // Dynamic width allocation strategy
      let widths;
      if (bothRentNeedSpace) {
      // Prioritize rent cards, minimize property/unit cards
      widths = {
          card1: rentCollectedNeedsSpace ? 250 : 220,
          card2: rentExpectedNeedsSpace ? 250 : 220,
          card3: 100, // Minimal for properties
          card4: 100, // Minimal for units
      };
      } else {
      // Balanced layout for normal values
      widths = {
          card1: 200,
          card2: 200,
          card3: 140,
          card4: 140,
      };
      }

      // Helper function to create text with smart wrapping
      const createTextElement = (value, x, y, fontSize, maxWidth) => {
      const valueStr = value.toString();
      const estimatedWidth = valueStr.length * (fontSize * 0.6);
      
      if (estimatedWidth <= maxWidth) {
          return `
          <text x="${x}" y="${y}" font-family="Inter, sans-serif" font-size="${fontSize}" font-weight="bold" fill="url(#textGradient)">
              ${valueStr}
          </text>
          `;
      } else {
          // Split into two lines
          const commaIndex = valueStr.indexOf(',');
          let firstLine, secondLine;
          
          if (commaIndex !== -1 && commaIndex > 2) {
          firstLine = valueStr.substring(0, commaIndex + 1);
          secondLine = valueStr.substring(commaIndex + 1);
          } else {
          const midPoint = Math.floor(valueStr.length / 2);
          firstLine = valueStr.substring(0, midPoint);
          secondLine = valueStr.substring(midPoint);
          }
          
          const adjustedFontSize = fontSize - 2; // Slightly smaller for wrapped text
          return `
          <text x="${x}" y="${y - 8}" font-family="Inter, sans-serif" font-size="${adjustedFontSize}" font-weight="bold" fill="url(#textGradient)">
              ${firstLine}
          </text>
          <text x="${x}" y="${y + 10}" font-family="Inter, sans-serif" font-size="${adjustedFontSize}" font-weight="bold" fill="url(#textGradient)">
              ${secondLine}
          </text>
          `;
      }
      };

      // Calculate positions
      const positions = {
      card1: 20,
      card2: 20 + widths.card1 + 20,
      card3: 20 + widths.card1 + 20 + widths.card2 + 20,
      card4: 20 + widths.card1 + 20 + widths.card2 + 20 + widths.card3 + 20,
      };

      const totalWidth = positions.card4 + widths.card4 + 20;

      const svgContent = `
      <svg width="${totalWidth}" height="200" xmlns="http://www.w3.org/2000/svg">
          <defs>
          <linearGradient id="textGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style="stop-color:#4E6CF0;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#8442E3;stop-opacity:1" />
          </linearGradient>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="2" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.1)"/>
          </filter>
          </defs>
          
          <!-- Card 1: Rent Collected -->
          <rect x="${positions.card1}" y="40" width="${widths.card1}" height="100" rx="5" fill="white" filter="url(#shadow)"/>
          <text x="${positions.card1 + 10}" y="65" font-family="Inter, sans-serif" font-size="12" font-weight="bold" fill="url(#textGradient)">
          Rent Collected (UGX)
          </text>
          ${createTextElement(rentCollectedStr, positions.card1 + 10, 100, 20, widths.card1 - 20)}
          
          <!-- Card 2: Rent Expected -->
          <rect x="${positions.card2}" y="40" width="${widths.card2}" height="100" rx="5" fill="white" filter="url(#shadow)"/>
          <text x="${positions.card2 + 10}" y="65" font-family="Inter, sans-serif" font-size="12" font-weight="bold" fill="url(#textGradient)">
          Rent Expected (UGX)
          </text>
          ${createTextElement(rentExpectedStr, positions.card2 + 10, 100, 20, widths.card2 - 20)}
          
          <!-- Card 3: Properties -->
          <rect x="${positions.card3}" y="40" width="${widths.card3}" height="100" rx="5" fill="white" filter="url(#shadow)"/>
          <text x="${positions.card3 + 10}" y="65" font-family="Inter, sans-serif" font-size="12" font-weight="bold" fill="url(#textGradient)">
          Properties
          </text>
          ${createTextElement(propertiesStr, positions.card3 + 10, 105, 36, widths.card3 - 20)}
          
          <!-- Card 4: Units -->
          <rect x="${positions.card4}" y="40" width="${widths.card4}" height="100" rx="5" fill="white" filter="url(#shadow)"/>
          <text x="${positions.card4 + 10}" y="65" font-family="Inter, sans-serif" font-size="12" font-weight="bold" fill="url(#textGradient)">
          Units
          </text>
          ${createTextElement(unitsStr, positions.card4 + 10, 105, 36, widths.card4 - 20)}
      </svg>
      `;
      
      return svgContent;
  }; 

  const handleDownloadPDF = async () => {
      try {

      const { data, startDate, endDate } = await fetchPayoutHistory(true);
              
      let filteredDataForDownload = transformPayouts(data?.data || []);

      const startDateFormatted = formatDateToISO(startDate, false).split('.')[0];
      const endDateFormatted = formatDateToISO(endDate, true).split('.')[0];
      let dateRangeText = `${startDateFormatted} to ${endDateFormatted}`;
      let searchTerm = search ? search.trim() : '';
      let selectedStatus = status ? status.trim() : 'All';

      if (filteredDataForDownload.length === 0) {
          showToast("warning", "No data available for the selected date range.");
          setIsPdfGenerating(false);
          return;
      }
      // Add progress indication for large datasets
      const MAX_PDF_RECORDS = 1000;
      if (filteredDataForDownload.length > MAX_PDF_RECORDS) {
          showToast("warning", `Dataset too large. Only first ${MAX_PDF_RECORDS} records will be included.`);
          filteredDataForDownload = filteredDataForDownload.slice(0, MAX_PDF_RECORDS);
      }
  
      applyPlugin(jsPDF);
      const pdf = new jsPDF('p', 'mm', 'a4');
  
      let pageHeight = pdf.internal.pageSize.height;
      let pageWidth = pdf.internal.pageSize.width;
  
      let yPosition = 20;
      let xPosition = 20;
      
      // Contact fields 
      pdf.setFontSize(7);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont("helvetica", "normal");
      
      pdf.text('Address:', xPosition, yPosition);
      pdf.text('TENAFIX LIMITED, P.O BOX 191810 KAMPALA UGANDA, CENTRAL, KAMPALA, KAWEMPE DIVISION, MULAGO 1, UPPER MAWANDA', xPosition + 15, yPosition + 0);

      pdf.text('Contact:', xPosition, yPosition + 5);
      pdf.text('+256 773 884476, +256 776 003035', xPosition + 15, yPosition + 5);

      pdf.text('Email:', xPosition, yPosition + 10);
      pdf.text('support@tenafix.co', xPosition + 15, yPosition + 10);
  
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.2);
      pdf.line(xPosition, yPosition + 12, pageWidth - 20, yPosition + 12);
  
      yPosition += 20;
  
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      pdf.text('Payouts Report', 20, yPosition);
  
      yPosition += 10;
  
      // Report metadata
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont("helvetica", "normal");
      pdf.text('Reporting Period:', 20, yPosition);
  
      pdf.setTextColor(0, 0, 0);
      pdf.text(dateRangeText, 50, yPosition);
      yPosition += 7;
  
      pdf.setTextColor(60, 60, 60);
      pdf.text('Generated on:', 20, yPosition);
      pdf.setTextColor(0, 0, 0);
      const now = new Date();
      const generationTime = `${now.toLocaleDateString()} at ${now.toLocaleTimeString()}`;
      pdf.text(generationTime, 50, yPosition);
      yPosition += 15;
  
      // Applied filters section
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      pdf.text('Applied Filters', 20, yPosition);
      yPosition += 8;
  
      pdf.setFontSize(9);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont("helvetica", "normal");
      
      if (searchTerm) {
          pdf.text(`• Search Filter: ${searchTerm}`, 25, yPosition);
          yPosition += 5;
      }
  
      if (selectedStatus) {
          pdf.text(`• Status Filter: ${selectedStatus}`, 25, yPosition);
          yPosition += 5;
      }
      
      pdf.text(`• Date Range: ${dateRangeText}`, 25, yPosition);
      yPosition += 10;
  
      // Create SVG InfoCard with gradients
      const svgContent = createInfoCardSVG(data?.infoCard);
  
      try {
          const infoCardCanvas = await svgToCanvas(svgContent, 800, 200);
          if (infoCardCanvas) {
              const infoCardImgData = infoCardCanvas.toDataURL('image/png');
              const infoCardWidth = 170; // Full width within margins
              const infoCardHeight = (200 * infoCardWidth) / 800; // Proportional height calculation
  
              pdf.addImage(infoCardImgData, 'PNG', 20, yPosition, infoCardWidth, infoCardHeight);
              yPosition += infoCardHeight - 10;
          }
      } catch (svgError) {
          showToast("error", "Failed to generate info card image. Please try again.");
      }
  
      const autoTableData = filteredDataForDownload.map(item => [
          item?.date || "N/A",           // Date / Time
          item?.landlordName || "N/A",     // Landlord Name
          item?.deposit || "N/A",      // Bank / Mobile Wallet
          item.rent ? `${item.rent.toFixed(2)} UGX` : "N/A" , //gross amount
          item.commission ? `${item.commission.toFixed(2)} UGX` : "N/A",  // commision 
          item.payments[0]?.netAmount ? `${item.payments[0].netAmount.toFixed(2)} UGX` : "N/A",    // Net amount paid (from sub-table)
          item.payments[0]?.transactionStatus || "N/A",       // Status (from sub-table)
          item.payments[0]?.relatedPayment || "N/A"   // Reference ID (from sub-table)
      ]);
  
      const tableHeaders = [
          'Date / Time',
          'Landlord Name', 
          'Bank / Mobile Wallet',
          'Gross Amount',
          'Platform Commission',
          'Net Amount Paid',
          'Status',
          'Reference ID'
      ];
  
      pdf.autoTable({
          head: [tableHeaders],
          body: autoTableData,
          startY: yPosition + 10,
          theme: 'grid',
          headStyles: {
          fillColor: [71, 114, 241],
          textColor: [255, 255, 255],
          fontSize: 8,
          fontStyle: 'bold',
          halign: 'center'
          },
          bodyStyles: {
          fontSize: 7,
          textColor: [0, 0, 0],
          cellPadding: 2
          },
          alternateRowStyles: {
          fillColor: [248, 249, 250] // Light gray for alternate rows
          },
          columnStyles: {
              0: { halign: 'center', cellWidth: 18 },     // Date / Time
              1: { halign: 'left', cellWidth: 20 },       // Landlord Name
              2: { halign: 'center', cellWidth: 34 },     // Bank / Mobile Wallet
              3: { halign: 'left', cellWidth: 20 },       // Gross Amount
              4: { halign: 'right', cellWidth: 20 },      // Platform Commission
              5: { halign: 'center', cellWidth: 20 },     // Net Amount Paid
              6: { halign: 'center', cellWidth: 21 },     // Status
              7: { halign: 'center', cellWidth: 22 },     // Reference ID
          },
          margin: { left: 20, right: 20, top: 20, bottom: 20 },
          styles: {
          overflow: 'linebreak',
          fontSize: 9
          }
      });
  
      // Add header and footer to all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
          pdf.setPage(i);
          
          const footerY = pageHeight - 15;
  
          // header section
          pdf.setFontSize(10);
          pdf.setTextColor(71, 114, 241); // Blue color for brand
          pdf.setFont("helvetica", "bold");
          pdf.text('Tenafix Limited', 20, 10);
          
          pdf.setFontSize(8);
          pdf.setTextColor(100, 100, 100);
          pdf.setFont("helvetica", "normal");
          pdf.text('Real Estate Management Software', 20, 14);
          
          pdf.addImage(Logo, 'PNG', pageWidth - 40, -5, 30, 30);
          
          // Footer section
          pdf.setFontSize(8);
          pdf.setTextColor(71, 114, 241);
          pdf.setFont("helvetica", "bold");
          pdf.text('Tenafix Limited', 20, footerY + 3);
          
          pdf.setTextColor(100, 100, 100);
          pdf.setFont("helvetica", "normal");
          pdf.text('|', 42, footerY + 3);
          pdf.text('www.tenafix.co', 44, footerY + 3);
          pdf.text('|', 64, footerY + 3);
          pdf.text('support@tenafix.co', 66, footerY + 3);
          
          // System generated note
          pdf.setFontSize(7);
          pdf.setTextColor(120, 120, 120);
          pdf.text('This is a system-generated report.', 20, footerY + 6);
          pdf.text('For assistance contact support@tenafix.co', 20, footerY + 9);
          
          // Page number (right aligned)
          pdf.setFontSize(8);
          pdf.setTextColor(100, 100, 100);
          const pageText = `Page ${i} of ${pageCount}`;
          const pageTextWidth = pdf.getTextWidth(pageText);
          pdf.text(pageText, pdf.internal.pageSize.width - pageTextWidth - 20, footerY + 8);
      }
  
      // Save PDF
      const localNow = new Date().toLocaleString().split(', ');
      const currentDate = `${localNow[0]}/${localNow[1].split(' ')[0]}`.replace(/[/:]/g, '-');
      pdf.save(`payouts-report-${currentDate}.pdf`);
      
      } catch (error) {
      showToast("error", "Failed to generate PDF. Please try again.");
      } finally {
      setIsPdfGenerating(false);
      }
    };

  const handleDownloadClick = () => {
    setIsPdfGenerating(true);
    setTimeout(() => handleDownloadPDF(), 0);
  }

  return (
    <div className="p-6">
      <div className="mb-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white px-[30px] py-[31px] rounded-[10px] shadow-[0_4px_4px_0_#0000000F] mt-[30px]">
        {/* Stat Cards */}
        <div className="grid gap-x-6 sm:gap-x-8 md:gap-x-10 lg:gap-x-[52px] gap-y-[30px]  grid-cols-1 xs:grid-cols-2 xl:grid-cols-4 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <InfoCard
              label="Rent Collected (UGX)"
              value={metrics.totalRentCollected}
              className=" max-h-[120px] mx-auto"
              labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
              valueStyle="font-inter font-semibold text-[25px] lg:text-[29px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
          <div>
            <InfoCard
              label="Rent Expected (UGX)"
              value={metrics.totalRentExpected}
              className=" max-h-[120px] mx-auto"
              labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
              valueStyle="font-inter font-semibold text-[25px] lg:text-[29px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
          <div>
            <InfoCard
              label="Properties"
              value={metrics.totalUniqueProperties}
              className=" max-h-[100px] mx-auto"
              labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              valueStyle="font-inter font-semibold text-[25px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
          <div>
            <InfoCard
              label="Units"
              value={metrics.totalUniqueUnits}
              className=" max-h-[100px] mx-auto"
              labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              valueStyle="font-inter font-semibold text-[25px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
        </div>
      </div>

      <div className="mb-[30px]">
        <TableHeader
          searchValue={search}
          onSearchChange={setSearch}
          onSearchSubmit={handleSearch}
          searchPlaceholder="Search by landlord name or reference"
          dateRangeFilter={{
            value: selectedRange,
            onChange: setSelectedRange,
            options: [
              { label: "All Time", value: "" },
              { label: "Today", value: "Today" },
              { label: "This Week", value: "This Week" },
              { label: "This Month", value: "This Month" },
              { label: "Last Month", value: "Last Month" },
            ],
          }}
          filters={[
            {
              name: "Status",
              value: status,
              onChange: setStatus,
              options: [
                { label: "All", value: "" },
                { label: "Success", value: "SUCCESS" },
                { label: "Processing", value: "PROCESSING" },
                { label: "Failed", value: "FAILED" },
              ],
            },
          ]}
          buttons={[
            {
              label: "Search",
              onClick: handleSearch,
              variant: "secondary",
              className:
                "bg-[#9333EA] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-3 rounded-[3.77px] uppercase w-[102px] ",
            },
            {
              label: "DOWNLOAD REPORT",
              onClick: handleDownloadClick,
              variant: "main",
              loading: isPdfGenerating,
              disabled: isPdfGenerating,
              className:
                "bg-[#C084FC] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-3 rounded-[3.77px] font-bold uppercase w-[154px] sm:w-[138px] md:w-[152px] lg:w-[154px] [@media(min-width:1080px)]:w-[184px] [@media(min-width:1708px)]:w-[247px] ",
            },
          ]}
          containerClass="bg-white  flex-wrap md:grid md:grid-cols-2 lg:grid lg:grid-cols-2 md:gap-y-[30px] [@media(min-width:1120px)]:grid [@media(min-width:1120px)]:grid-cols-3 [@media(min-width:1516px)]:grid [@media(min-width:1516px)]:grid-cols-4 justify-between  p-3 sm:p-4 md:p-6 lg:p-[30px] "
          filterDropdownClass="w-full md:w-full lg:w-full [@media(min-width:1516px)]:w-[247px] [@media(min-width:1516px)]:w-full"
          searchInputClass="text-gray-700 h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] md:w-full [@media(min-width:1516px)]:w-[247px] [@media(min-width:1516px)]:w-full"
          datePickerClass="gap-2"
          searchTooltip="Search by landlord name or reference"
          datePickerInputClass="w-full sm:w-full md:w-full lg:w-full [@media(min-width:1516px)]:w-full [@media(min-width:1708px)]:w-[247px] border"
          buttonWrapperClass="justify-center sm:justify-start  md:gap-[10px] lg:gap-[10px] [@media(min-width:1516px)]:gap-[10px] [@media(min-width:1300px)]:ml-[380px] [@media(min-width:1120px)]:ml-[280px]  [@media(min-width:1516px)]:ml-[0px]  [@media(min-width:1516px)]:justify-end"
          buttonClass="uppercase tracking-wide flex justify-center"
        />
      </div>

      <div className="bg-white rounded-[8px] border border-[#E5E5E5]">
      {isFetchingPayouts && !isPdfGenerating ? (
          <div className="relative top-0 mt-0 left-0 right-0 bottom-0 flex justify-center items-center">
            <Loader color="#4772F1" />
          </div>
          ) : isEmptyData ? (
        <NotFound text1="No data" text2="No payout history found for your search criteria." />
      ) : errorPayouts ? (
        <NotFound text1="Error fetching payouts" text2={errorPayouts?.message || "An error occurred."} />
      ) : (
        <LandLordTable
          dataSource={transformedPayouts}
          columns={columns}
          hasSubTable={true}
          subTableConfig={subTableConfig}
          rowKey="key"
        />
      )}      
      </div>


      {!isEmptyData && (
      <Pagination
        current={currentPage || 1}
        total={pagination?.totalDocuments || 0}
        pageSize={itemsPerPage}
        onChange={handlePageChange}
        showSizeChanger={false}
        className="custom-pagination"
      />
  )}
    </div>
  );
};

export default AdminPayoutPage;